import { useRef } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useRevealAll } from "../hooks/useReveal";
import { useSeo, breadcrumbLd, SITE_NAME } from "../hooks/useSeo";
import { BLOG_POSTS, getPost } from "../data/blogPosts";

export default function BlogPost() {
  const { slug } = useParams();
  const post = getPost(slug);
  const ref = useRef(null);
  useRevealAll(ref, [slug]);
  const path = `/blog/${slug}`;
  useSeo({
    title: post ? `${post.seoTitle} | ${SITE_NAME}` : `Blog | ${SITE_NAME}`,
    description: post?.metaDescription,
    path,
    image: post?.image,
    type: "article",
    keywords: post?.keywords.join(", "),
    jsonLd: post && {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "BlogPosting",
          headline: post.title,
          description: post.metaDescription,
          image: [post.image],
          datePublished: post.datePublished,
          dateModified: post.dateModified,
          inLanguage: "hu-HU",
          keywords: post.keywords.join(", "),
          articleSection: post.category,
          mainEntityOfPage: window.location.origin + path,
          author: { "@type": "Organization", name: SITE_NAME, url: window.location.origin },
          publisher: {
            "@type": "Organization",
            name: SITE_NAME,
            logo: { "@type": "ImageObject", url: window.location.origin + "/logo.png" },
          },
        },
        breadcrumbLd([["Kezdőlap", "/"], ["Blog", "/blog"], [post.title, path]]),
      ],
    },
  });

  if (!post) return <Navigate to="/blog" replace />;
  const others = BLOG_POSTS.filter((p) => p.slug !== slug);

  return (
    <main ref={ref} data-testid="page-blog-post" className="pt-28 md:pt-32 bg-[color:var(--he-bg)]">
      <nav aria-label="Morzsamenü" className="he-container py-8" data-testid="blog-breadcrumb">
        <ol className="flex flex-wrap items-center gap-2 overline-xs text-[color:var(--he-ink-soft)]">
          <li><Link to="/" className="he-link">Kezdőlap</Link></li>
          <li aria-hidden>/</li>
          <li>
            <Link to="/blog" className="he-link whitespace-nowrap" data-testid="blog-post-back">Blog</Link>
          </li>
          <li aria-hidden>/</li>
          <li aria-current="page" className="truncate max-w-[60vw] md:max-w-md text-[color:var(--he-ink)]">{post.title}</li>
        </ol>
      </nav>

      <article data-testid="blog-post-article">
        <header className="he-container pt-6 pb-12">
          <div className="max-w-4xl">
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2 overline-xs text-[color:var(--he-ink-soft)] reveal">
              <span className="text-[color:var(--he-gold)]">{post.category}</span>
              <span className="w-6 h-px bg-[color:var(--he-line)]" aria-hidden />
              <time dateTime={post.datePublished}>{post.date}</time>
              <span className="w-6 h-px bg-[color:var(--he-line)]" aria-hidden />
              <span>{post.readTime} olvasás</span>
              <span className="w-6 h-px bg-[color:var(--he-line)]" aria-hidden />
              <span>{SITE_NAME}</span>
            </div>
            <h1
              data-testid="blog-post-title"
              className="mt-6 font-serif-display tracking-[-0.022em] reveal delay-1"
              style={{ fontWeight: 400, fontSize: "clamp(2.2rem, 5vw, 4.2rem)", lineHeight: 1.08 }}
            >
              {post.title}
            </h1>
          </div>
        </header>

        <figure className="he-container reveal delay-2">
          <div className="overflow-hidden aspect-[16/9] bg-[color:var(--he-line)]">
            <img
              data-testid="blog-post-image"
              src={post.image}
              alt={post.imageAlt}
              width="1600"
              height="900"
              fetchpriority="high"
              className="w-full h-full object-cover"
            />
          </div>
          <figcaption className="sr-only">{post.imageAlt}</figcaption>
        </figure>

        <div data-testid="blog-post-body" className="he-container py-16 md:py-24">
          <div className="max-w-[720px] mx-auto">
            {post.blocks.map((b, i) => (
              <Block key={i} block={b} />
            ))}

            <div className="mt-16 pt-10 border-t border-[color:var(--he-line)]">
              <Link to={post.cta.to} className="btn-ink" data-testid="blog-post-cta">
                <span>{post.cta.label}</span>
                <ArrowRight size={14} />
              </Link>
            </div>
          </div>
        </div>
      </article>

      {others.length > 0 && (
        <aside className="bg-[color:var(--he-cream)] py-20 md:py-28" data-testid="blog-related">
          <div className="he-container">
            <p className="overline mb-8 mag-marker"><span>Olvasd tovább</span></p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
              {others.map((o) => (
                <Link key={o.slug} to={`/blog/${o.slug}`} className="group flex gap-6 items-center" data-testid={`blog-related-${o.slug}`}>
                  <img src={o.image} alt={o.imageAlt} loading="lazy" className="w-28 h-28 md:w-36 md:h-36 object-cover shrink-0" />
                  <div>
                    <p className="overline-xs text-[color:var(--he-gold)] mb-2">{o.category}</p>
                    <h2 className="font-serif-display text-xl md:text-2xl leading-snug group-hover:text-[color:var(--he-gold)] transition-colors">{o.title}</h2>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </aside>
      )}
    </main>
  );
}

function Block({ block: [type, content] }) {
  if (type === "lead")
    return (
      <p className="font-serif-display text-[1.35rem] md:text-[1.55rem] leading-[1.55] text-[color:var(--he-ink)] mb-8">
        {content}
      </p>
    );
  if (type === "h2")
    return (
      <h2
        className="font-serif-display tracking-[-0.018em] mt-14 mb-5"
        style={{ fontWeight: 400, fontSize: "clamp(1.6rem, 2.6vw, 2.1rem)", lineHeight: 1.18 }}
      >
        {content}
      </h2>
    );
  if (type === "h3")
    return <h3 className="overline-xs text-[color:var(--he-gold)] mt-8 mb-3">{content}</h3>;
  if (type === "quote")
    return (
      <blockquote className="my-12 pl-8 border-l border-[color:var(--he-gold)] font-serif-display italic text-[1.5rem] md:text-[1.8rem] leading-[1.4]">
        {content}
      </blockquote>
    );
  if (type === "ul")
    return (
      <ul className="space-y-2.5 my-5 font-light leading-[1.85]">
        {content.map((it, i) => (
          <li key={i} className="pl-6 relative">
            <span className="absolute left-0 top-[0.9em] w-3 h-px bg-[color:var(--he-gold)]" aria-hidden />
            {it}
          </li>
        ))}
      </ul>
    );
  if (type === "dl")
    return (
      <dl className="my-6 divide-y divide-[color:var(--he-line)] border-y border-[color:var(--he-line)]">
        {content.map(([term, desc]) => (
          <div key={term} className="py-5 grid sm:grid-cols-[180px_1fr] gap-2 sm:gap-8">
            <dt className="overline-xs text-[color:var(--he-gold)] pt-1">{term}</dt>
            <dd className="font-light leading-[1.85]">{desc}</dd>
          </div>
        ))}
      </dl>
    );
  return <p className="font-light leading-[1.9] text-[color:var(--he-ink)] mb-5">{content}</p>;
}
